import { useState, useRef, useEffect } from 'react'
const useAudioRecorder = (initialAudioURL = null) => {
  const [isRecording, setIsRecording] = useState(false)
  const [audioURL, setAudioURL] = useState(initialAudioURL)
  const [recordingTime, setRecordingTime] = useState(0)
  const [playbackTime, setPlaybackTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const timerRef = useRef(null)
  const playbackTimerRef = useRef(null)
  const audioRef = useRef(initialAudioURL ? new Audio(initialAudioURL) : null)

  // Sync audioURL with initialAudioURL if it changes
  useEffect(() => {
    setAudioURL(initialAudioURL)
    if (initialAudioURL) {
      if (audioRef.current) {
        audioRef.current.pause()
      }
      audioRef.current = new Audio(initialAudioURL)
      attachAudioEvents(audioRef.current)
    }
  }, [initialAudioURL])

  const attachAudioEvents = (audio) => {
    // Reset state when playback ends
    audio.addEventListener('ended', () => {
      setIsPlaying(false)
      setPlaybackTime(0)
    })

    // Clear playback timer when audio changes
    audio.addEventListener('timeupdate', () => {
      setPlaybackTime(Math.floor(audio.currentTime))
    })
  }

  const startRecording = async () => {
    setIsRecording(true)
    setRecordingTime(0)
    audioChunksRef.current = []
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const mediaRecorder = new MediaRecorder(stream)
    mediaRecorderRef.current = mediaRecorder

    mediaRecorder.ondataavailable = (event) => {
      audioChunksRef.current.push(event.data)
    }

    mediaRecorder.start()
    timerRef.current = setInterval(() => {
      setRecordingTime((time) => time + 1)
    }, 1000)
  }

  const stopRecording = async () => {
    setIsRecording(false)
    clearInterval(timerRef.current)

    const mediaRecorder = mediaRecorderRef.current
    mediaRecorder.stop()

    return new Promise((resolve) => {
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm'
        })
        resolve(audioBlob)
      }
    })
  }

  const cancelRecording = () => {
    if (!isRecording || !mediaRecorderRef.current) return

    const mediaRecorder = mediaRecorderRef.current
    mediaRecorder.stop()
    mediaRecorderRef.current = null
    audioChunksRef.current = [] // Clear recorded data
    clearInterval(timerRef.current) // Clear the recording timer

    setIsRecording(false)
    setRecordingTime(0) // Reset recording time
  }

  const uploadRecording = async (audioBlob) => {
    setIsUploading(true)
    let form = new FormData()
    form.append('file', audioBlob)

    const response = await fetch(
      'https://learnpodseditornodeserver.knomadixapp.com/upload',
      {
        method: 'POST',
        credentials: 'same-origin',
        body: form
      }
    )

    if (!response.ok) throw Error('Failed to upload audio.')

    const result = await response.json()
    const { url } = result

    setAudioURL(url)
    setIsUploading(false)
    return url
  }

  const deleteRecording = async () => {
    if (!audioURL) return

    const fileId = audioURL.split('/').pop()
    const DELETE_FILE_URL = `https://learnpodseditornodeserver.knomadixapp.com/delete?file=${fileId}`

    await fetch(DELETE_FILE_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: fileId })
    })

    setAudioURL(null)
  }

  const playRecording = () => {
    if (audioRef.current) {
      setIsPlaying(true)
      setIsPaused(false)
      audioRef.current.play()
    }
  }

  const pausePlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      setIsPaused(true)
    }
  }

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setPlaybackTime(0)
      setIsPlaying(false)
      setIsPaused(false)
    }
  }

  return {
    isRecording,
    audioURL,
    recordingTime,
    playbackTime,
    isPlaying,
    isPaused,
    isUploading,
    startRecording,
    stopRecording,
    cancelRecording,
    uploadRecording,
    deleteRecording,
    playRecording,
    pausePlayback,
    stopPlayback
  }
}
export default useAudioRecorder
